import { useTranslation } from "@cronika/locales";
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, Modal, ModalContent, cn } from "@cronika/ui";
import { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import { getBetPeriodId, getBetPeriodSeconds } from "@/types/index";
import { getServerNowMs } from "@/shared/utils/serverTime";
import { profileSettingsStore } from "@/shared/stores/ProfileSettingsStore";
import type { TradingPeriodMessage } from "@cronika/proto/common/trading_pb";
import { Down, Finish, Minus, Plus, Time, Up } from "@cronika/assets/icons";
import type { TTimeSelectorProps } from "./types";
import { ETimeSelectorType } from "../../types";
import { DURATION_MAX_SECONDS, DURATION_MIN_SECONDS, ETimeSelectorMode } from "./config";
import { HmsStepper } from "./HmsStepper";
import { InlineClock } from "./InlineClock";
import { QuickButton } from "./QuickButton";
import { SegmentedToggle } from "./SegmentedToggle";
import {
  clampDuration,
  computeTimeQuickPresets,
  findClosestPeriod,
  formatClock,
  formatDurationLabel,
  getHmsInTimezone,
  getMinPeriodSeconds,
  getSodInTimezone,
  hmsToSeconds,
  pad2,
  resolveDurationPresets,
  secondsToHms,
  secondsUntilSod,
  stepQuarterHour,
  type THms,
} from "./lib";

const serverNow = (): Date => new Date(getServerNowMs());

// `now` (and therefore nowSod/manualSeconds) only advances once per second, on
// the setInterval tick — but the moment it does, a target seeded/corrected to
// sit at *exactly* minPeriodSeconds ahead reads as manualSeconds === minPeriodSeconds
// this instant and minPeriodSeconds - 1 (invalid, isManualTooEarly) the very next
// tick, before that tick's own correction has run. A few seconds of slack means
// ordinary 1s tick granularity never lands exactly on the boundary.
const MIN_PERIOD_SAFETY_MARGIN_SECONDS = 3;

const LS_MODE = "ts:mode";
const LS_DURATION_SECS = "ts:duration-secs";
const LS_QUICK_OPEN = "ts:quick";

const lsGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const lsSet = (key: string, val: string) => {
  try {
    localStorage.setItem(key, val);
  } catch {
    /* ignore */
  }
};

export const TimeSelector: FC<TTimeSelectorProps> = ({
  type,
  onChange,
  betPeriods: externalBetPeriods,
  value,
  marketCloseSeconds,
  onCustomDurationChange,
  onTimeTargetChange,
  maxDurationSeconds,
  minDurationSeconds,
  initialCustomDurationSeconds,
}) => {
  const { t } = useTranslation("trading");
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const isHorizontal = useMediaQuery("(max-height: 390px)");

  const timezone = profileSettingsStore.timezone || "";

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<ETimeSelectorMode>(() => {
    const saved = lsGet(LS_MODE);
    return saved === ETimeSelectorMode.TIME ? ETimeSelectorMode.TIME : ETimeSelectorMode.DURATION;
  });
  const [isQuickOpen, setIsQuickOpen] = useState(() => lsGet(LS_QUICK_OPEN) === "1");
  const [now, setNow] = useState<Date>(() => serverNow());
  const [isOnboardingOverride, setIsOnboardingOverride] = useState(false);

  const betPeriods = useMemo(() => externalBetPeriods ?? [], [externalBetPeriods]);

  const isPeriodDisabled = useCallback(
    (period: TradingPeriodMessage): boolean =>
      marketCloseSeconds != null && getBetPeriodSeconds(period) >= marketCloseSeconds,
    [marketCloseSeconds],
  );

  const currentPeriod = useMemo(() => {
    if (!betPeriods.length) return undefined;
    if (value) return betPeriods.find((p) => getBetPeriodId(p) === value) ?? betPeriods[0];
    return betPeriods[0];
  }, [betPeriods, value]);

  const currentSeconds = getBetPeriodSeconds(currentPeriod);

  // Whole-seconds-of-day for "now". Time-mode maths compare this against the
  // draft's own seconds-of-day so everything stays integer (no ms, no rounding
  // drift that would make the min-bump effect oscillate forever).
  const nowSod = getSodInTimezone(now, timezone);

  // Draft values for the manual ("Custom") steppers. Duration keeps a relative
  // H/M/S; Time keeps an absolute wall-clock target. Both resolve to the closest
  // real bet period on commit so payouts stay server-authoritative.
  const initialSeconds = currentSeconds || minDurationSeconds || 60;
  const [durationDraft, setDurationDraft] = useState<THms>(() => {
    const min = minDurationSeconds ?? DURATION_MIN_SECONDS;
    const max = maxDurationSeconds || DURATION_MAX_SECONDS;
    // Priority: server setting → localStorage → default
    const serverVal = initialCustomDurationSeconds != null ? initialCustomDurationSeconds : null;
    const lsVal = lsGet(LS_DURATION_SECS);
    const raw = serverVal ?? (lsVal ? Number(lsVal) : initialSeconds);
    return secondsToHms(Math.max(min, Math.min(max, raw)));
  });
  const [timeDraft, setTimeDraft] = useState<THms>(() => {
    const target = new Date(now.getTime() + initialSeconds * 1000);
    return getHmsInTimezone(target, timezone);
  });
  const timeDraftRef = useRef(timeDraft);
  timeDraftRef.current = timeDraft;

  useEffect(() => {
    const handleOnboardingValue = (event: Event) => {
      const detail = (event as CustomEvent<{ durationSeconds?: number }>).detail;
      if (detail?.durationSeconds === undefined) return;
      setMode(ETimeSelectorMode.DURATION);
      setIsOnboardingOverride(true);
      setDurationDraft(secondsToHms(detail.durationSeconds));
      onCustomDurationChange?.(detail.durationSeconds);
    };
    window.addEventListener("cronika:onboarding-set-values", handleOnboardingValue);
    return () => window.removeEventListener("cronika:onboarding-set-values", handleOnboardingValue);
  }, [onCustomDurationChange]);

  // Refresh dynamic "now"-derived state whenever the panel is (re)opened.
  // durationDraft is intentionally NOT reset here — the panel should open
  // showing whatever the user last typed or selected, not snap to a preset.
  useEffect(() => {
    if (!isOpen) return;
    const current = serverNow();
    setNow(current);
    const secs = currentSeconds || minDurationSeconds || 60;
    const target = new Date(current.getTime() + secs * 1000);
    setTimeDraft(getHmsInTimezone(target, timezone));
  }, [isOpen]);

  useEffect(() => {
    if (initialCustomDurationSeconds == null) return;
    setDurationDraft(secondsToHms(initialCustomDurationSeconds));
  }, [initialCustomDurationSeconds]);

  useEffect(() => {
    lsSet(LS_MODE, mode);
  }, [mode]);
  useEffect(() => {
    lsSet(LS_QUICK_OPEN, isQuickOpen ? "1" : "0");
  }, [isQuickOpen]);
  useEffect(() => {
    lsSet(LS_DURATION_SECS, String(hmsToSeconds(durationDraft)));
  }, [durationDraft]);

  // When the pair changes (betPeriods replaced with genuinely different periods),
  // sync the duration display to the currently-selected period.
  // We compare by the first period's ID so MobX re-renders that produce a new
  // array reference with identical content (e.g. stats updates) don't reset the
  // user's manually typed value.
  const firstPeriodIdRef = useRef<string | undefined>(
    externalBetPeriods?.[0] ? getBetPeriodId(externalBetPeriods[0]) : undefined,
  );
  useEffect(() => {
    const first = betPeriods[0];
    const id = first ? getBetPeriodId(first) : undefined;
    if (id === firstPeriodIdRef.current) return;
    const wasEmpty = firstPeriodIdRef.current === undefined;
    firstPeriodIdRef.current = id;
    // First betPeriods fill ([] → [periods]): skip reset if server restored a custom value.
    if (wasEmpty && initialCustomDurationSeconds != null) return;
    // Pair switch with custom duration active: keep the draft unchanged.
    if (!wasEmpty && initialCustomDurationSeconds != null) return;
    // Sync the duration draft to the actually-selected period. If `value` matches
    // a period in the new pair's list, use that period's duration so the display
    // stays in sync with the selection. Otherwise fall back to the first period.
    const active = value ? (betPeriods.find((p) => getBetPeriodId(p) === value) ?? first) : first;
    const secs = getBetPeriodSeconds(active);
    if (secs > 0) setDurationDraft(secondsToHms(secs));
  }, [betPeriods, initialCustomDurationSeconds, value]);

  const isTime = mode === ETimeSelectorMode.TIME;
  useEffect(() => {
    if (!isOpen && !isTime) return;
    const id = setInterval(() => setNow(serverNow()), 1000);
    return () => clearInterval(id);
  }, [isOpen, isTime]);

  useEffect(() => {
    if (mode !== ETimeSelectorMode.TIME) return;
    const current = serverNow();
    setNow(current);
    const secs = Math.max(currentSeconds || 60, minPeriodSeconds + MIN_PERIOD_SAFETY_MARGIN_SECONDS);
    const target = new Date(current.getTime() + secs * 1000);
    setTimeDraft(getHmsInTimezone(target, timezone));
  }, [mode]);

  // useLayoutEffect (not useEffect): profileSettingsStore.timezone can change more
  // often than the user explicitly picking a new one in this modal — a MobX
  // reaction in ProfileSettingsStore re-syncs it from userStore.user on every
  // profile refetch (balance polls, session refresh, etc.), so this can fire many
  // times per session. With a plain effect, the corrected timeDraft only lands
  // *after* the browser has already painted one render with the new nowSod
  // against the still-stale draft — a real, visible red flash each time. Running
  // synchronously before paint means that render never happens.
  const prevTimezoneRef = useRef(timezone);
  useLayoutEffect(() => {
    const prevTz = prevTimezoneRef.current;
    prevTimezoneRef.current = timezone;
    if (prevTz === timezone || mode !== ETimeSelectorMode.TIME) return;
    const current = serverNow();
    const prevSod = getSodInTimezone(current, prevTz);
    const midnightTs = Math.floor(current.getTime() / 1000) - prevSod;
    let targetTs = midnightTs + hmsToSeconds(timeDraftRef.current);
    // Re-anchoring only carries the *existing* gap-from-now across the timezone
    // switch — it doesn't itself guarantee that gap ever cleared minPeriodSeconds.
    // profileSettingsStore.timezone can flip far more often than a real user
    // action (see comment above), so this can fire every few seconds; if it never
    // clamps, an already-too-soon target just gets carried forward invalid forever
    // instead of ever reaching the push-up effect's corrected value. Clamp here too.
    const nowSecs = Math.floor(current.getTime() / 1000);
    if (targetTs - nowSecs < minPeriodSeconds) {
      targetTs = nowSecs + minPeriodSeconds + MIN_PERIOD_SAFETY_MARGIN_SECONDS;
    }
    setTimeDraft(getHmsInTimezone(new Date(targetTs * 1000), timezone));
    // minPeriodSeconds intentionally not in deps: it's read via closure for the
    // clamp above, but a bare minPeriodSeconds change (without timezone/mode
    // changing) is already handled by the push-up effect below — re-declaring it
    // as a dep here would need minPeriodSeconds hoisted above this effect.
  }, [timezone, mode]);

  const selectPeriod = useCallback((period: TradingPeriodMessage) => onChange?.(getBetPeriodId(period)), [onChange]);

  const minPeriodSeconds = useMemo(() => {
    if (minDurationSeconds != null) return minDurationSeconds;
    if (betPeriods.length) return getMinPeriodSeconds(betPeriods);
    return DURATION_MIN_SECONDS;
  }, [betPeriods, minDurationSeconds]);

  /** Longest tradable period — the ceiling for manual entry in both modes. */
  const maxPeriodSeconds = maxDurationSeconds || DURATION_MAX_SECONDS;

  /** Pick a period straight off the predefined ladder (clears any custom length). */
  const selectPredefined = useCallback(
    (period: TradingPeriodMessage) => {
      setIsOnboardingOverride(false);
      selectPeriod(period);
      // Keep the always-visible stepper in sync with the chosen preset.
      setDurationDraft(secondsToHms(getBetPeriodSeconds(period)));
      onCustomDurationChange?.(null);
    },
    [selectPeriod, onCustomDurationChange],
  );

  /**
   * Commit a duration. The closest predefined period is still selected so the
   * payout preview stays on the server-defined ladder, but when the length was
   * entered manually we also report the exact seconds so the caller can send a
   * CUSTOM bet instead of the snapped admin TTL.
   */
  const commitSeconds = useCallback(
    (seconds: number, custom = false) => {
      setIsOnboardingOverride(false);
      const period = findClosestPeriod(betPeriods, clampDuration(seconds), marketCloseSeconds);
      if (period) selectPeriod(period);
      const isExactPreset = Boolean(period && getBetPeriodSeconds(period) === seconds);
      onCustomDurationChange?.(custom && !isExactPreset ? seconds : null);
    },
    [betPeriods, marketCloseSeconds, selectPeriod, onCustomDurationChange],
  );

  // Auto-select last enabled period when the market close makes the selection invalid.
  useEffect(() => {
    if (!currentPeriod || marketCloseSeconds == null || !isPeriodDisabled(currentPeriod)) return;
    const lastEnabled = [...betPeriods].reverse().find((p) => !isPeriodDisabled(p));
    if (lastEnabled) {
      selectPeriod(lastEnabled);
      setDurationDraft(secondsToHms(getBetPeriodSeconds(lastEnabled)));
    }
  }, [marketCloseSeconds]);

  // Default selection once periods load.
  useEffect(() => {
    if (betPeriods.length && !value && betPeriods[0]) onChange?.(getBetPeriodId(betPeriods[0]));
  }, [betPeriods]);

  // For pairs with no presets, auto-emit the initial custom duration on mount
  // so the caller has a valid duration without requiring user interaction.
  // Use the actual durationDraft (already clamped to min/max) rather than bare
  // minDurationSeconds — avoids mismatch when localStorage had a different value.
  useEffect(() => {
    if (betPeriods.length || !minDurationSeconds) return;
    onCustomDurationChange?.(hmsToSeconds(durationDraft));
  }, [betPeriods.length, minDurationSeconds]);

  // ---- Presets -------------------------------------------------------------

  const durationPresets = useMemo(() => resolveDurationPresets(betPeriods), [betPeriods]);
  const timePresets = useMemo(() => computeTimeQuickPresets(now, timezone), [now, timezone]);

  // ---- Field / header summaries -------------------------------------------

  const label = mode === ETimeSelectorMode.TIME ? t("time-mode-label") : t("time-label");
  const modeIcon =
    mode === ETimeSelectorMode.TIME ? <Finish className="h-3.75 w-3.75" /> : <Time className="h-3.75 w-3.75" />;

  // The compact field and the stepper edit these drafts directly. The panel
  // header shows the friendly duration label ("1 min") or the target clock.
  const headerValue =
    mode === ETimeSelectorMode.TIME ? formatClock(timeDraft) : formatDurationLabel(hmsToSeconds(durationDraft));

  // ---- +/- stepping through the duration ladder ---------------------------

  const currentIndex = useMemo(
    () => (currentPeriod ? betPeriods.findIndex((p) => getBetPeriodId(p) === getBetPeriodId(currentPeriod)) : -1),
    [currentPeriod, betPeriods],
  );

  const lastEnabledIndex = useMemo(() => {
    for (let i = betPeriods.length - 1; i >= 0; i--) if (!isPeriodDisabled(betPeriods[i]!)) return i;
    return -1;
  }, [betPeriods, isPeriodDisabled]);

  const isFirstPeriod = currentIndex === 0;
  const isLastPeriod = currentIndex >= lastEnabledIndex;

  const goToNextPeriod = () => {
    if (currentIndex < 0) return;
    const nextIndex = Math.min(currentIndex + 1, lastEnabledIndex < 0 ? betPeriods.length - 1 : lastEnabledIndex);
    const next = betPeriods[nextIndex];
    if (next && !isPeriodDisabled(next)) selectPredefined(next);
  };

  const goToPrevPeriod = () => {
    if (currentIndex < 0) return;
    const prev = betPeriods[Math.max(currentIndex - 1, 0)];
    if (prev) selectPredefined(prev);
  };

  // ---- Stepper change handlers --------------------------------------------

  /** Seconds from now until the manually entered expiry (negative = in the past). */
  const manualSeconds = useMemo(() => {
    if (mode === ETimeSelectorMode.TIME) return secondsUntilSod(hmsToSeconds(timeDraft), nowSod);
    return hmsToSeconds(durationDraft);
  }, [mode, nowSod, timeDraft, durationDraft]);

  // An expiry already in the past, sooner than the shortest tradable period,
  // farther than the max allowed duration, or past the market close is not selectable.
  const isManualTooEarly = manualSeconds < minPeriodSeconds;
  const isManualTooLate =
    manualSeconds > maxPeriodSeconds || (marketCloseSeconds != null && manualSeconds >= marketCloseSeconds);
  const isManualInvalid = !isOnboardingOverride && (isManualTooEarly || isManualTooLate);

  // When the manual input is invalid, clear any stale custom duration so the
  // caller can't silently fall back to a previously-committed out-of-range value.
  useEffect(() => {
    if (isManualInvalid) onCustomDurationChange?.(null);
  }, [isManualInvalid]);

  // When the pair switches to one with a higher minimum tradable period (e.g.
  // forex 1 min → crypto 3 min), snap the duration draft up to the new minimum.
  // Without this, the betPeriods effect can leave durationDraft stuck below
  // minPeriodSeconds permanently, causing the field to stay red on the new pair.
  useEffect(() => {
    const current = hmsToSeconds(durationDraft);
    if (current >= minPeriodSeconds) return;
    setDurationDraft(secondsToHms(minPeriodSeconds));
  }, [minPeriodSeconds]);

  // Report the absolute expiry (unix seconds) whenever Time mode holds a valid
  // selection, so the caller can send a FIXED bet at that exact wall-clock time.
  useEffect(() => {
    if (!onTimeTargetChange) return;
    if (mode !== ETimeSelectorMode.TIME || isManualInvalid) {
      onTimeTargetChange(null);
      return;
    }
    // now + manualSeconds, not a bare midnight-anchor + hmsToSeconds(timeDraft):
    // manualSeconds already rolls into tomorrow when the target's clock time
    // has passed today (see secondsUntilSod) — reusing that here keeps this in
    // sync with the validity check above instead of re-deriving the target
    // seconds-of-day without accounting for the same day rollover.
    onTimeTargetChange(Math.floor(now.getTime() / 1000) + manualSeconds);
  }, [mode, isManualInvalid, manualSeconds, now, onTimeTargetChange]);

  // In Time mode the actionable limit is the earliest selectable wall clock;
  // in Duration mode it is the shortest tradable length.
  const minHintValue = useMemo(() => {
    if (mode !== ETimeSelectorMode.TIME) return formatDurationLabel(minPeriodSeconds);
    const earliest = new Date(now.getTime() + minPeriodSeconds * 1000);
    return `${pad2(earliest.getHours())}:${pad2(earliest.getMinutes())}:${pad2(earliest.getSeconds())}`;
  }, [mode, now, minPeriodSeconds]);

  const maxHintValue = useMemo(() => {
    if (marketCloseSeconds != null && manualSeconds >= marketCloseSeconds) {
      return formatDurationLabel(marketCloseSeconds - 1);
    }
    if (manualSeconds > maxPeriodSeconds) {
      if (mode === ETimeSelectorMode.TIME) {
        const latest = new Date(now.getTime() + maxPeriodSeconds * 1000);
        return `${pad2(latest.getHours())}:${pad2(latest.getMinutes())}:${pad2(latest.getSeconds())}`;
      }
      return formatDurationLabel(maxPeriodSeconds);
    }
    return null;
  }, [mode, now, maxPeriodSeconds, marketCloseSeconds, manualSeconds]);

  const onDurationDraftChange = (next: THms) => {
    const total = hmsToSeconds(next);
    const effectiveMax =
      marketCloseSeconds != null ? Math.min(maxPeriodSeconds, marketCloseSeconds - 1) : maxPeriodSeconds;
    if (total > effectiveMax) {
      const clamped = secondsToHms(effectiveMax);
      setDurationDraft(clamped);
      if (effectiveMax >= minPeriodSeconds) commitSeconds(effectiveMax, true);
      return;
    }
    if (total < minPeriodSeconds) {
      const clamped = secondsToHms(minPeriodSeconds);
      setDurationDraft(clamped);
      commitSeconds(minPeriodSeconds, true);
      return;
    }
    setDurationDraft(next);
    commitSeconds(total, true);
  };

  const onTimeDraftChange = (next: THms) => {
    const sod = hmsToSeconds(next);
    const diff = secondsUntilSod(sod, nowSod);
    const effectiveMaxDiff =
      marketCloseSeconds != null ? Math.min(maxPeriodSeconds, marketCloseSeconds - 1) : maxPeriodSeconds;
    if (diff > effectiveMaxDiff) {
      const clampedSod = (nowSod + effectiveMaxDiff) % (24 * 3600);
      const clamped = secondsToHms(clampedSod);
      setTimeDraft(clamped);
      if (effectiveMaxDiff >= minPeriodSeconds) commitSeconds(effectiveMaxDiff, true);
      return;
    }
    if (diff < minPeriodSeconds) {
      const clampedSod = (nowSod + minPeriodSeconds) % (24 * 3600);
      const clamped = secondsToHms(clampedSod);
      setTimeDraft(clamped);
      commitSeconds(minPeriodSeconds, true);
      return;
    }
    setTimeDraft(next);
    commitSeconds(diff, true);
  };

  const onSelectTimePreset = (durationSeconds: number, targetMs: number) => {
    setTimeDraft(getHmsInTimezone(new Date(targetMs), timezone));
    commitSeconds(durationSeconds, false);
  };

  const nudgeUp = isTime ? () => onTimeDraftChange(stepQuarterHour(timeDraft, 1)) : goToNextPeriod;
  const nudgeDown = isTime ? () => onTimeDraftChange(stepQuarterHour(timeDraft, -1)) : goToPrevPeriod;
  const nudgeUpDisabled = manualSeconds >= maxPeriodSeconds || (!isTime && (isLastPeriod || !currentPeriod));
  const nudgeDownDisabled = isTime ? false : isFirstPeriod || !currentPeriod;

  useLayoutEffect(() => {
    if (!isTime) return;
    if (manualSeconds >= minPeriodSeconds) return;
    const next = secondsToHms((nowSod + minPeriodSeconds + MIN_PERIOD_SAFETY_MARGIN_SECONDS) % (24 * 3600));

    if (next.hours === timeDraft.hours && next.minutes === timeDraft.minutes && next.seconds === timeDraft.seconds)
      return;
    setTimeDraft(next);
  }, [nowSod, isTime, minPeriodSeconds, timeDraft, manualSeconds]);

  const stepperLabels = {
    hours: t("time-units.hours"),
    minutes: t("time-units.minutes"),
    seconds: t("time-units.seconds"),
  };

  const presetGrid =
    mode === ETimeSelectorMode.TIME ? (
      <div className="grid grid-cols-3 gap-2">
        {timePresets.map((preset) => {
          const disabled =
            preset.durationSeconds < minPeriodSeconds ||
            preset.durationSeconds > maxPeriodSeconds ||
            (marketCloseSeconds != null && preset.durationSeconds >= marketCloseSeconds);
          const active =
            timeDraft.seconds === 0 && `${pad2(timeDraft.hours)}:${pad2(timeDraft.minutes)}` === preset.label;
          return (
            <QuickButton
              key={preset.key}
              active={active}
              disabled={disabled}
              onClick={() => onSelectTimePreset(preset.durationSeconds, preset.targetMs)}
            >
              {preset.label}
            </QuickButton>
          );
        })}
      </div>
    ) : (
      <div className="grid grid-cols-3 gap-2">
        {durationPresets.map((preset) => {
          const disabled = !preset.period || isPeriodDisabled(preset.period);
          const active = Boolean(
            preset.period && currentPeriod && getBetPeriodId(preset.period) === getBetPeriodId(currentPeriod),
          );
          return (
            <QuickButton
              key={preset.key}
              active={active}
              disabled={disabled}
              onClick={() => preset.period && selectPredefined(preset.period)}
            >
              {preset.label}
            </QuickButton>
          );
        })}
      </div>
    );

  const panel = (
    <div className="flex w-full flex-col gap-3">
      <div className="flex min-w-0 items-center gap-2.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-warm/15 text-warning">
          {modeIcon}
        </div>
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-[11px] uppercase leading-3 tracking-wide text-default-500">{label}</span>
          <span className="truncate text-[16px] font-semibold leading-5">{headerValue}</span>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <HmsStepper
          labels={stepperLabels}
          invalid={isManualInvalid}
          compareToNow={isTime}
          value={isTime ? timeDraft : durationDraft}
          onChange={isTime ? onTimeDraftChange : onDurationDraftChange}
          timezone={timezone}
        />
        {isManualInvalid && (
          <p className="text-[11px] leading-4 text-danger">
            {isManualTooLate && maxHintValue
              ? t("time-max-hint", { max: maxHintValue })
              : t("time-min-hint", { min: minHintValue })}
          </p>
        )}
      </div>

      {(betPeriods.length > 0 || mode === ETimeSelectorMode.TIME) && (
        <div className="flex flex-col rounded-b-2xl bg-default-500/15 -mx-4 -mb-4 px-4 pb-2 pt-1">
          <button
            type="button"
            onClick={() => setIsQuickOpen((o) => !o)}
            aria-expanded={isQuickOpen}
            className={cn(
              "flex items-center justify-center gap-1 text-[13px] font-medium text-default-500 transition-colors hover:text-white",
              isQuickOpen && "pb-4",
            )}
          >
            {t("time-quick")}
            {isQuickOpen ? <Up className="h-3.5 w-3.5" /> : <Down className="h-3.5 w-3.5" />}
          </button>
          {isQuickOpen && presetGrid}
        </div>
      )}
    </div>
  );

  const modeToggle = (
    <div onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
      <SegmentedToggle<ETimeSelectorMode>
        value={mode}
        onChange={setMode}
        size="sm"
        segments={[
          {
            value: ETimeSelectorMode.DURATION,
            icon: <Time className="w-[15px] h-[15px]" />,
            "aria-label": t("time-label"),
          },
          {
            value: ETimeSelectorMode.TIME,
            icon: <Finish className="h-[20px] w-[20px]" />,
            "aria-label": t("time-mode-label"),
          },
        ]}
      />
    </div>
  );

  const inlineClock = (
    <InlineClock
      className="md:text-[16px] text-[14px]"
      value={isTime ? timeDraft : durationDraft}
      invalid={isManualInvalid}
      compareToNow={isTime}
      timezone={timezone}
      onChange={isTime ? onTimeDraftChange : onDurationDraftChange}
    />
  );

  const triggerContent = (
    <div className="flex h-12 w-full items-center justify-between gap-1 px-2 py-[2px]">
      <div className="flex flex-col items-start gap-1">
        <p className="text-[12px] leading-3 text-default-500">{label}</p>
        {inlineClock}
      </div>
      {modeToggle}
    </div>
  );

  const triggerButton = (
    <div
      className={cn(
        "flex w-full cursor-pointer items-center rounded-lg border border-glass/10 bg-glass/4",
        isHorizontal && "h-10",
      )}
    >
      {triggerContent}
    </div>
  );

  if (isMobile && !isHorizontal) {
    return (
      <div data-tour-target="duration-block" className="flex flex-col items-center gap-2">
        <div className="w-full" onClick={() => setIsOpen(true)}>
          {triggerButton}
        </div>
        <Modal open={isOpen} onOpenChange={setIsOpen}>
          <ModalContent
            overlayStyle={{ display: "none", pointerEvents: "none" }}
            hideCloseButton
            backdrop="opaque"
            className="flex flex-col !rounded-[16px] border-white/10 bg-glass/8 backdrop-blur-xl"
          >
            <div className="p-4">{panel}</div>
          </ModalContent>
        </Modal>
      </div>
    );
  }

  if (!isHorizontal && type === ETimeSelectorType.DROPDOWN) {
    return (
      <div data-tour-target="duration-block" className="flex w-full flex-col gap-2">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger asChild>
            <div className="flex w-full cursor-pointer flex-col rounded-lg bg-glass/4 px-2.5 py-1.5 ">
              <p className="text-[12px] text-default-500">{label}</p>
              <InlineClock
                className="text-[16px]"
                value={isTime ? timeDraft : durationDraft}
                invalid={isManualInvalid}
                compareToNow={isTime}
                timezone={timezone}
                onChange={isTime ? onTimeDraftChange : onDurationDraftChange}
              />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side={isTablet ? "top" : "left"}
            align="start"
            sideOffset={23}
            radius="lg"
            className="w-80 border-white/10 bg-glass/8 p-4 backdrop-blur-md z-[10004]"
          >
            <div className="w-full" onKeyDown={(e) => e.stopPropagation()}>
              {panel}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="grid grid-cols-[1fr_30px_1fr] gap-1">
          <Button
            variant="ghost-v2"
            isIconOnly
            radius="lg"
            className="w-full h-5.5"
            onClick={nudgeDown}
            disabled={nudgeDownDisabled}
          >
            <Minus />
          </Button>
          <Button
            variant="ghost-v2"
            isIconOnly
            radius="lg"
            className="w-full h-5.5"
            onClick={() => setMode(isTime ? ETimeSelectorMode.DURATION : ETimeSelectorMode.TIME)}
            aria-label={isTime ? t("time-label") : t("time-mode-label")}
          >
            {modeIcon}
          </Button>
          <Button
            data-tour-target="duration-plus"
            variant="ghost-v2"
            isIconOnly
            radius="lg"
            className="w-full h-5.5"
            onClick={nudgeUp}
            disabled={nudgeUpDisabled}
          >
            <Plus />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <div
        data-tour-target="duration-block"
        className={cn(
          "flex w-full items-center justify-between rounded-lg border border-glass/10 bg-glass/4 px-4 py-1",
          isHorizontal ? "min-h-10.5" : "min-h-14",
        )}
      >
        {isHorizontal && (isTime || betPeriods.length > 0) && (
          <Button
            variant="ghost-v2"
            isIconOnly
            size="lg"
            className="h-5 w-7"
            onClick={nudgeDown}
            disabled={nudgeDownDisabled}
          >
            <Minus />
          </Button>
        )}
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              "flex flex-1 cursor-pointer items-start",
              isHorizontal ? "flex-row items-center justify-center gap-2" : "flex-col",
            )}
          >
            {!isHorizontal && (
              <div className="flex w-full items-center justify-between">
                <p className="text-[12px] text-default-500">{label}</p>
                {modeToggle}
              </div>
            )}
            {inlineClock}
          </div>
        </DropdownMenuTrigger>
        {isHorizontal && (isTime || betPeriods.length > 0) && (
          <Button
            variant="ghost-v2"
            isIconOnly
            size="lg"
            className="h-5 w-7"
            onClick={nudgeUp}
            disabled={nudgeUpDisabled}
          >
            <Plus />
          </Button>
        )}
        {!isHorizontal && (isTime || betPeriods.length > 0) && (
          <div className="flex flex-col gap-1 ml-2">
            <Button
              data-tour-target="duration-plus"
              variant="ghost-v2"
              isIconOnly
              size="sm"
              className="h-5 w-7"
              onClick={nudgeUp}
              disabled={nudgeUpDisabled}
            >
              <Plus />
            </Button>
            <Button
              variant="ghost-v2"
              isIconOnly
              size="sm"
              className="h-5 w-7"
              onClick={nudgeDown}
              disabled={nudgeDownDisabled}
            >
              <Minus />
            </Button>
          </div>
        )}
      </div>
      <DropdownMenuContent
        side={isTablet ? "top" : "left"}
        align="start"
        sideOffset={23}
        radius="lg"
        className="w-80 border-white/10 bg-glass/8 p-4 backdrop-blur-md z-[10004]"
      >
        <div className="w-full" onKeyDown={(e) => e.stopPropagation()}>
          {panel}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
